import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { ManualImportResultDto, RedditCrawlStatusDto } from '../dto/AdminOperationsDtos';
import { UserRole } from '../../domain/value-objects/UserRole';
import { adminOperationsService } from '../services/AdminOperationsService';
import { notificationService } from '../../infrastructure/notifications';
import { queryKeys } from './queryKeys';

/**
 * Trigger OSM import mutation params
 *
 * IMPORTANT: Must provide EITHER subAreaId OR regionId (not both):
 * - subAreaId: Import single SubArea
 * - regionId: Import ALL SubAreas in region
 */
interface TriggerOsmImportParams {
  subAreaId: string | null;
  regionId?: string;
  userRole: UserRole;
}

/**
 * Trigger OSM Import Mutation (Admin Only)
 *
 * Manually triggers an OpenStreetMap data import for a specific SubArea.
 * Imports Places, Amenities, and Businesses for the selected geographic area.
 *
 * @returns Mutation hook with import result data
 *
 * @example
 * const triggerImport = useTriggerOsmImportMutation();
 * triggerImport.mutate({
 *   subAreaId: '123e4567-e89b-12d3-a456-426614174000',
 *   userRole: 'Admin'
 * });
 */
export const useTriggerOsmImportMutation = (): UseMutationResult<
  ManualImportResultDto,
  Error,
  TriggerOsmImportParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subAreaId, regionId, userRole }: TriggerOsmImportParams): Promise<ManualImportResultDto> => {
      if (userRole !== 'Admin') {
        throw new Error('Only admins can trigger OSM imports');
      }
      return adminOperationsService.triggerOsmImport(subAreaId, regionId);
    },
    onSuccess: (data): void => {
      // Invalidate businesses list cache (new businesses imported)
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });

      // Show success notification with import statistics
      const businessCount = data.businessesImported + data.businessesUpdated;
      notificationService.showSuccess(
        `Import completed successfully! ${businessCount} businesses processed (${data.businessesImported} new, ${data.businessesUpdated} updated).`,
        'Import Successful'
      );
    },
    onError: (error: Error): void => {
      console.error('OSM import failed:', error);
      notificationService.showError(
        error.message || 'Failed to trigger OSM import. Please try again.',
        'Import Failed'
      );
    }
  });
};

/**
 * Reddit Crawl Status Query (Admin Only)
 *
 * Gets the current status of the Reddit crawler background service.
 * NOTE: Full implementation planned for Sessions 005/006.
 * Currently returns placeholder status from backend.
 *
 * @param userRole - Current user's role
 * @param enabled - Whether the query should run (default: false, manual polling)
 * @returns Query hook with crawl status data
 *
 * @example
 * const { data: status } = useRedditCrawlStatusQuery('Admin', true);
 * // Enable polling every 3 seconds when monitoring active crawl
 */
export const useRedditCrawlStatusQuery = (
  userRole?: UserRole,
  enabled: boolean = false
): UseQueryResult<RedditCrawlStatusDto, Error> => {
  return useQuery({
    queryKey: queryKeys.redditCrawl.status(),
    queryFn: async (): Promise<RedditCrawlStatusDto> => {
      return adminOperationsService.getRedditCrawlStatus();
    },
    enabled: enabled && userRole === 'Admin',
    refetchInterval: enabled ? 3000 : false, // Poll every 3 seconds when enabled
    retry: 2
  });
};

/**
 * Start Reddit Crawl Mutation (Admin Only)
 *
 * Manually starts the Reddit crawler background service.
 * NOTE: Full implementation planned for Sessions 005/006.
 * Currently backend returns placeholder "not yet implemented" message.
 *
 * @returns Mutation hook
 *
 * @example
 * const startCrawl = useStartRedditCrawlMutation();
 * startCrawl.mutate({ userRole: 'Admin' });
 */
export const useStartRedditCrawlMutation = (): UseMutationResult<void, Error, { userRole: UserRole }, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userRole }: { userRole: UserRole }): Promise<void> => {
      if (userRole !== 'Admin') {
        throw new Error('Only admins can start Reddit crawl');
      }
      return adminOperationsService.startRedditCrawl();
    },
    onSuccess: (): void => {
      // Invalidate crawl status
      void queryClient.invalidateQueries({ queryKey: queryKeys.redditCrawl.status() });

      // Show info notification (not fully implemented yet)
      notificationService.showInfo(
        'Reddit crawl control will be fully implemented in Sessions 005/006.',
        'Feature Coming Soon'
      );
    },
    onError: (error: Error): void => {
      console.error('Start Reddit crawl failed:', error);
      notificationService.showError(
        error.message || 'Failed to start Reddit crawl.',
        'Start Failed'
      );
    }
  });
};

/**
 * Stop Reddit Crawl Mutation (Admin Only)
 *
 * Manually stops the Reddit crawler background service.
 * NOTE: Full implementation planned for Sessions 005/006.
 * Currently backend returns placeholder "not yet implemented" message.
 *
 * @returns Mutation hook
 *
 * @example
 * const stopCrawl = useStopRedditCrawlMutation();
 * stopCrawl.mutate({ userRole: 'Admin' });
 */
export const useStopRedditCrawlMutation = (): UseMutationResult<void, Error, { userRole: UserRole }, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userRole }: { userRole: UserRole }): Promise<void> => {
      if (userRole !== 'Admin') {
        throw new Error('Only admins can stop Reddit crawl');
      }
      return adminOperationsService.stopRedditCrawl();
    },
    onSuccess: (): void => {
      // Invalidate crawl status
      void queryClient.invalidateQueries({ queryKey: queryKeys.redditCrawl.status() });

      // Show info notification (not fully implemented yet)
      notificationService.showInfo(
        'Reddit crawl control will be fully implemented in Sessions 005/006.',
        'Feature Coming Soon'
      );
    },
    onError: (error: Error): void => {
      console.error('Stop Reddit crawl failed:', error);
      notificationService.showError(
        error.message || 'Failed to stop Reddit crawl.',
        'Stop Failed'
      );
    }
  });
};
